/**
 * The hidden context the web app puts in front of every message to the agent. The agent's prompts
 * route on these exact strings (`[view: story_development]` starts the create skill), so they are
 * pinned by sendContract.test and must not change with the UI.
 */
export type WorkspaceView = 'outline' | 'studio' | 'narration' | 'storyboard' | 'lab' | 'blog';

export function workspaceContext(opts: {
  conversationId: string;
  title?: string;
  activeTab: WorkspaceView | string;
  activeSlide?: { id: string; title: string };
}): string {
  const parts = [`[project_id: ${opts.conversationId}]`];
  if (opts.title) parts.push(`[project: "${opts.title}"]`);
  if (opts.activeTab === 'studio') {
    parts.push('[view: studio]');
    if (opts.activeSlide) parts.push(`[active_slide: ${opts.activeSlide.id} "${opts.activeSlide.title}"]`);
  } else if (opts.activeTab === 'scenes') {
    // The build view is new to the UI only; the agent keeps seeing the storyboard view it knows.
    parts.push('[view: storyboard]');
  } else if (['outline', 'narration', 'storyboard', 'lab', 'blog'].includes(opts.activeTab)) {
    parts.push(`[view: ${opts.activeTab}]`);
  }
  return parts.join(' ');
}

export const storyDevelopmentContext = (conversationId: string): string =>
  `[project_id: ${conversationId}] [view: story_development]`;

const ACTIVE_SLIDE = / \[active_slide: (\S+) "(.*)"\]$/;

/** The scene a context sends with the message (the studio's stage), as workspaceContext writes it last. */
export function activeSlideIn(context?: string): { id: string; title: string } | null {
  const m = context?.match(ACTIVE_SLIDE);
  return m ? { id: m[1], title: m[2] } : null;
}

export const withoutActiveSlide = (context: string): string => context.replace(ACTIVE_SLIDE, '');
